import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

import { PriceMessage } from '../../../../types/PricesMessage';
import { Avatar } from '../../../cdl/Avatar/Avatar';
import { Text } from '../../../cdl/Text/Text';
import { formatNumber } from '../../../common/helpers/formatNumber.helper';
import { formatPrice } from '../../../common/helpers/formatPrice.helper';
import { formatTotalPrice } from '../../../common/helpers/formatTotal.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { InfoPopover } from '../../../common/InfoPopover/InfoPopover';
import { PriceBreakdownItem, PriceItemsBreakdown } from '../../../common/PriceItemsBreakdown/PriceItemsBreakdown';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { MetaInformation } from '../common/OfferCard/MetaInformation';

const ReferenceCard = styled.div`
    display: grid;
    grid-template-columns: 1fr 300px;
    border: 1px solid transparent;
    border-radius: ${theme.radii[8]};
    background-color: ${theme.colors.background.subtle};
    color: ${theme.colors.foreground.muted};
`;

const ReferenceSummary = styled.div`
    display: grid;
    grid-template-columns: 40px 1fr auto;
    gap: ${theme.space[16]};
    padding: ${theme.space[16]};
    align-items: center;
`;

const ReferenceSpacer = styled.div`
    display: grid;
    align-content: start;
    row-gap: ${theme.space[8]};
    border-left: 1px solid ${theme.colors.border.muted};
    padding: ${theme.space[16]};
`;

interface DefaultContractReferenceCardProps {
    price?: PriceMessage;
    isPending: boolean;
}

const DefaultContractReferenceCardSkeleton = () => {
    return (
        <ReferenceCard>
            <ReferenceSummary>
                <Skeleton height={40} width={40} borderRadius={theme.radii[6]} />

                <Box display="grid" gridTemplateRows="24px 16px">
                    <Skeleton height={18} width="150px" />
                    <Skeleton height={12} width="220px" />
                </Box>

                <Box display="grid" gridTemplateRows="24px 16px" justifyItems="end">
                    <Skeleton height={18} width="100px" />
                    <Skeleton height={12} width="150px" />
                </Box>
            </ReferenceSummary>

            <ReferenceSpacer>
                <Skeleton height={22} />
                <Skeleton height={16} width="210px" />
            </ReferenceSpacer>
        </ReferenceCard>
    );
};

export const DefaultContractReferenceCard = ({ price, isPending }: DefaultContractReferenceCardProps) => {
    if (isPending) {
        return <DefaultContractReferenceCardSkeleton />;
    }

    if (!price) {
        return null;
    }

    const contractName = price.contract?.name;
    const hasPartialPricing = !!price.partialTotalPrice && !price.totalPrice;
    const totalPrice = price.totalPrice ?? price.partialTotalPrice;
    const ppl = price.ppl ?? price.partialPpl;
    const volume = hasPartialPricing ? price.partialVolume : price.volume;
    const priceBreakdownItems: PriceBreakdownItem[] = price.items.map((item) => ({
        productName: item.product?.name,
        baseProductName: item.product?.name,
        unitPrice: item.price,
        totalPrice: item.total,
        isTranslationSuccessful: !item.translationFailed,
    }));
    const hasPriceBreakdown = priceBreakdownItems.some((item) => item.unitPrice || item.totalPrice);

    return (
        <ReferenceCard>
            <ReferenceSummary>
                <Avatar name={price.supplier.name} imageUrl={price.supplier.supplierGroup.logo?.url} />

                <Box display="flex" flexDirection="column" minWidth={0}>
                    <Text variant="subtitle1" color="foreground.muted" style={{ whiteSpace: 'nowrap' }}>
                        {price.supplier.name}
                    </Text>

                    <Box display="flex" alignItems="center" gap={4} minWidth={0}>
                        <Text
                            variant="extraSmall"
                            color="foreground.muted"
                            style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {contractName}
                        </Text>
                    </Box>
                </Box>

                <Box display="flex" flexDirection="column" alignItems="end">
                    <Box display="flex" alignItems="center" gap={8}>
                        {hasPriceBreakdown ? (
                            <InfoPopover side="left" align="start">
                                <PriceItemsBreakdown items={priceBreakdownItems} />
                            </InfoPopover>
                        ) : null}

                        <Text variant="subtitle1" color="foreground.muted">
                            {totalPrice ? formatTotalPrice(totalPrice) : Characters.EN_DASH}
                        </Text>
                    </Box>

                    {hasPartialPricing ? (
                        <Text variant="extraSmall" color="foreground.muted">
                            {translate('compare.partialPricing')}
                        </Text>
                    ) : null}

                    {totalPrice ? (
                        <Box display="flex" flexWrap="nowrap">
                            {ppl ? (
                                <Text variant="extraSmall" color="foreground.muted" style={{ whiteSpace: 'nowrap' }}>
                                    {translate('order.detail.perLiter', {
                                        price: formatPrice(ppl),
                                    })}
                                    {Characters.NO_BREAK_SPACE}
                                    {Characters.SOLIDUS}
                                    {Characters.NO_BREAK_SPACE}
                                </Text>
                            ) : null}

                            {volume !== undefined ? (
                                <Text variant="extraSmall" color="foreground.muted">
                                    {formatNumber({
                                        number: volume,
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    })}
                                    {Characters.NO_BREAK_SPACE}L
                                </Text>
                            ) : null}
                        </Box>
                    ) : null}
                </Box>
            </ReferenceSummary>

            <ReferenceSpacer>
                <MetaInformation variant="neutral">
                    <IconInfoCircle width={16} height={16} />
                    <Text variant="extraSmall" color="foreground.muted" fontWeight={600}>
                        {translate('order.detail.defaultContractReference.referenceOnly')}
                    </Text>
                </MetaInformation>
                <Text variant="action" color="foreground.muted">
                    {translate('order.detail.defaultContractReference.description')}
                </Text>
            </ReferenceSpacer>
        </ReferenceCard>
    );
};
